import React, { useState, useEffect } from "react";
import { supabase } from "../../../lib/supabase";
import { Database, Search, ExternalLink, Filter, Plus, Pencil } from "lucide-react";
import { RelationChips, RelationChip } from "./RelationChips";
import { useIntegrationGraph } from "../../../hooks/useIntegrationGraph";

interface Connector {
  id: string;
  name: string;
  type: string;
  status: "active" | "inactive" | "in progress";
  hotel_base: number;
  active_hotels: number;
  scopes: string[];
  trigger_objects?: string[];
  code: string;
  created_at: string;
  updated_at: string;
}

// Type for the webhook data format used in the database
// Each entry is an object with a single key-value pair where
// the key is the object_type and the value is the URL
type WebhookData = Record<string, string>[];

interface ConnectorEditFormProps {
  connector: Connector | null;
  onClose: () => void;
  onSave: () => void;
}

function ConnectorEditForm({
  connector,
  onClose,
  onSave,
}: ConnectorEditFormProps) {
  const [formData, setFormData] = useState<Connector>(
    connector || {
      id: "",
      name: "",
      type: "pms",
      status: "inactive",
      hotel_base: 0,
      active_hotels: 0,
      scopes: [],
      trigger_objects: [],
      code: "",
      created_at: "",
      updated_at: "",
    }
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [availableScopes, setAvailableScopes] = useState<
    { code: string; name: string }[]
  >([]);

  // Check if webhooks scope is selected
  const isWebhooksSelected = formData.scopes?.some(
    (scope) => scope.toLowerCase() === "webhooks"
  );

  useEffect(() => {
    loadScopes();
  }, []);

  // Clear trigger_objects when "webhooks" scope is removed
  useEffect(() => {
    if (!isWebhooksSelected && formData.trigger_objects?.length) {
      setFormData({
        ...formData,
        trigger_objects: [],
      });
    }
  }, [isWebhooksSelected]);

  const loadScopes = async () => {
    try {
      const { data, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("code, name")
        .order("name");

      if (scopesError) throw scopesError;
      setAvailableScopes(data || []);
    } catch (err) {
      console.error("Error loading scopes:", err);
      setError("Failed to load scopes");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name?.trim()) {
        throw new Error("Name is required");
      }

      // Validate hotel numbers
      if (formData.active_hotels > formData.hotel_base) {
        throw new Error("Active hotels cannot exceed total hotel base");
      }

      // For new connectors, auto-generate code from name
      // Make sure we have a code value, even when editing (in case it was null in the database)
      const code = formData.code
        ? formData.code // Use existing code when editing if it exists
        : formData.name.trim().toLowerCase().replace(/\s+/g, "_"); // Generate new code otherwise

      const { error: updateError } = await supabase.from("connectors").upsert({
        id: formData.id || undefined,
        name: formData.name.trim(),
        type: formData.type,
        status: formData.status,
        hotel_base: formData.hotel_base,
        active_hotels: formData.active_hotels,
        scopes: formData.scopes,
        trigger_objects: formData.trigger_objects,
        code: code,
        updated_at: new Date().toISOString(),
      });

      if (updateError) throw updateError;

      // Only update integrations if we're editing an existing connector (not creating a new one)
      // and if the connector type is "pms" (as we're dealing with PMS scopes)
      if (formData.id && formData.type === "pms") {
        // Step 1: Find all hotels associated with this connector from the "credentials" table
        const { data: hotels, error: hotelsError } = await supabase
          .from("credentials")
          .select("hotel_id")
          .eq("connector_code", code)
          .eq("connector_type", "pms")
          .eq("is_primary", true);

        if (hotelsError) throw hotelsError;

        if (hotels && hotels.length > 0) {
          // Get all hotel IDs
          const hotelIds = hotels.map((hotel) => hotel.hotel_id);

          // Step 2: Check the "integrations" table to retrieve all SaaS connected to these hotels
          const { data: integrations, error: integrationsError } =
            await supabase
              .from("integrations")
              .select("id, saas_id, hotel_id, scopes, webhooks")
              .in("hotel_id", hotelIds);

          if (integrationsError) throw integrationsError;

          if (integrations && integrations.length > 0) {
            // Get unique SaaS IDs
            const saasIds = Array.from(
              new Set(integrations.map((integration) => integration.saas_id))
            );

            // Step 3: Retrieve scopes of all these SaaS from the "saas_details" table
            const { data: saasDetails, error: saasError } = await supabase
              .from("saas_details")
              .select("id, scopes, webhooks")
              .in("id", saasIds);

            if (saasError) throw saasError;

            if (saasDetails) {
              // Create a map of SaaS ID to its scopes and webhooks
              const saasMap = new Map();
              saasDetails.forEach((saas) => {
                saasMap.set(saas.id, {
                  scopes: saas.scopes || [],
                  webhooks: saas.webhooks,
                });
              });

              // Step 4: Update each integration with the intersection of connector and SaaS scopes
              for (const integration of integrations) {
                const saasInfo = saasMap.get(integration.saas_id);

                if (saasInfo) {
                  // Calculate intersection of connector scopes and SaaS scopes
                  const intersectionScopes = formData.scopes.filter((scope) =>
                    saasInfo.scopes.includes(scope)
                  );

                  // Check if scopes have changed to avoid unnecessary updates
                  const scopesChanged =
                    JSON.stringify(intersectionScopes.sort()) !==
                    JSON.stringify((integration.scopes || []).sort());

                  // Always process if there's a "webhooks" scope in either the current or new scopes
                  // This ensures we update when trigger_objects change, even if scopes don't change
                  const hasWebhookInCurrent = (integration.scopes || []).some(
                    (scope: string) => scope.toLowerCase() === "webhooks"
                  );
                  const hasWebhookInNew = intersectionScopes.some(
                    (scope: string) => scope.toLowerCase() === "webhooks"
                  );
                  const shouldProcessWebhooks =
                    hasWebhookInCurrent || hasWebhookInNew;

                  if (scopesChanged || shouldProcessWebhooks) {
                    // Determine if webhooks scope is in the intersection
                    const hasWebhookScope = intersectionScopes.some(
                      (scope: string) => scope.toLowerCase() === "webhooks"
                    );

                    // Initialize filtered webhooks
                    let filteredWebhooks: WebhookData | null = null;

                    // Handle webhooks if webhooks is in the intersection
                    if (hasWebhookScope && saasInfo.webhooks) {
                      // Get the connector's trigger_objects
                      const connectorTriggerObjects =
                        formData.trigger_objects || [];

                      // Filter webhooks to only include entries for objects that are common
                      // between saas webhooks and connector trigger_objects
                      filteredWebhooks = saasInfo.webhooks.filter(
                        (webhookEntry: Record<string, string>) => {
                          // Each entry is an object with a single key-value pair
                          const objectType = Object.keys(webhookEntry)[0];
                          const isCommon =
                            connectorTriggerObjects.includes(objectType);
                          return isCommon;
                        }
                      );

                      // If no common objects, don't include webhooks in the scopes
                      if (filteredWebhooks && filteredWebhooks.length === 0) {
                        // Remove webhooks from intersection scopes
                        const webhookIndex = intersectionScopes.findIndex(
                          (scope: string) => scope.toLowerCase() === "webhooks"
                        );
                        if (webhookIndex !== -1) {
                          intersectionScopes.splice(webhookIndex, 1);
                        }
                        filteredWebhooks = null;
                      }
                    }

                    // Check if webhooks have changed to avoid unnecessary updates
                    const currentWebhooks = integration.webhooks || null;
                    const webhooksChanged =
                      JSON.stringify(filteredWebhooks) !==
                      JSON.stringify(currentWebhooks);

                    // Only update if either scopes or webhooks have changed
                    if (scopesChanged || webhooksChanged) {
                      // Update the integration
                      const { error: updateIntegrationError } = await supabase
                        .from("integrations")
                        .update({
                          scopes: intersectionScopes,
                          // Update webhooks only if webhooks is in the intersection and there are common objects
                          webhooks: filteredWebhooks,
                        })
                        .eq("id", integration.id);

                      if (updateIntegrationError) {
                        console.error(
                          `Error updating integration ${integration.id}:`,
                          updateIntegrationError
                        );
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }

      await onSave();
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update connector"
      );
      console.error("Error updating connector:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {formData.id
              ? `Edit Connector: ${connector?.name}`
              : "Add New Connector"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <ExternalLink className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 text-sm rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name
            </label>
            <input
              type="text"
              value={formData.name || ""}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="pms">PMS</option>
              <option value="crs">CRS</option>
              <option value="crm">CRM</option>
              <option value="rms">RMS</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as Connector["status"],
                })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="in progress">In Progress</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Hotel Base
              </label>
              <input
                type="number"
                min="0"
                value={formData.hotel_base}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    hotel_base: parseInt(e.target.value) || 0,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Active Hotels
              </label>
              <input
                type="number"
                min="0"
                max={formData.hotel_base}
                value={formData.active_hotels}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    active_hotels: parseInt(e.target.value) || 0,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Scopes
            </label>
            <div className="max-h-48 overflow-y-auto border rounded-md p-3 bg-gray-50">
              <div className="grid grid-cols-2 gap-3">
                {availableScopes.map((scope) => (
                  <label
                    key={scope.code}
                    className="flex items-center p-2 bg-white rounded-md border border-gray-200 hover:bg-blue-50 hover:border-blue-200 transition-colors cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={formData.scopes?.includes(scope.code)}
                      onChange={(e) => {
                        const newScopes = e.target.checked
                          ? [...(formData.scopes || []), scope.code]
                          : (formData.scopes || []).filter(
                              (code) => code !== scope.code
                            );
                        setFormData({ ...formData, scopes: newScopes });
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-3"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {scope.name}
                    </span>
                  </label>
                ))}
              </div>
              {availableScopes.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No scopes available
                </div>
              )}
            </div>
          </div>

          {/* Trigger Objects dropdown - only shown when "webhooks" scope is selected */}
          {isWebhooksSelected && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trigger Objects
              </label>

              {/* Show messages when no scopes are available or selected */}
              {availableScopes.length === 0 && (
                <div className="text-center py-4 bg-gray-50 border rounded-md">
                  <p className="text-gray-500">No scopes available</p>
                </div>
              )}

              {availableScopes.length > 0 &&
                formData.scopes?.filter(
                  (scope) => scope.toLowerCase() !== "webhooks"
                ).length === 0 && (
                  <div className="text-center py-4 bg-gray-50 border rounded-md">
                    <p className="text-gray-500">
                      Select at least one scope (other than Webhooks) to add a
                      trigger object
                    </p>
                  </div>
                )}

              {/* Only show dropdown when there are eligible scopes selected */}
              {availableScopes.length > 0 &&
                formData.scopes?.filter(
                  (scope) => scope.toLowerCase() !== "webhooks"
                ).length > 0 && (
                  <select
                    multiple
                    value={formData.trigger_objects || []}
                    onChange={(e) => {
                      // Convert HTMLCollection to array of selected values
                      const selectedOptions = Array.from(
                        e.target.selectedOptions
                      ).map((option) => option.value);
                      setFormData({
                        ...formData,
                        trigger_objects: selectedOptions,
                      });
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 h-48"
                  >
                    {availableScopes
                      .filter(
                        (scope) =>
                          formData.scopes?.includes(scope.code) &&
                          scope.code.toLowerCase() !== "webhooks"
                      )
                      .map((scope) => (
                        <option
                          key={`trigger-${scope.code}`}
                          value={scope.code}
                        >
                          {scope.name}
                        </option>
                      ))}
                  </select>
                )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : formData.id
                ? "Save Changes"
                : "Create Connector"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface PMSTableProps {
  /** Search term pushed in when arriving from another listing's relation chip. */
  initialSearch?: string | null;
  onInitialSearchConsumed?: () => void;
  onOpenHotel?: (hotelName: string) => void;
}

export function PMSTable({
  initialSearch,
  onInitialSearchConsumed,
  onOpenHotel,
}: PMSTableProps = {}) {
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(
    null
  );
  const [pmsScopes, setPmsScopes] = useState<{ code: string; name: string }[]>(
    []
  );
  const {
    graph,
    connectorEmptyLabel,
    connectorEmptyVariant,
  } = useIntegrationGraph();

  useEffect(() => {
    loadScopes();
  }, []);

  useEffect(() => {
    if (initialSearch === undefined || initialSearch === null) return;
    setSearchTerm(initialSearch);
    // Clear the facets, otherwise a leftover filter can hide the row we just
    // navigated to and the link looks broken.
    setStatusFilter("all");
    setTypeFilter("all");
    onInitialSearchConsumed?.();
  }, [initialSearch, onInitialSearchConsumed]);

  const hotelChipsFor = (connectorCode: string): RelationChip[] =>
    (graph.hotelsByConnectorCode.get(connectorCode) ?? []).map((hotel) => ({
      key: hotel.id,
      label: hotel.name,
      hint: hotel.code,
    }));

  const loadScopes = async () => {
    try {
      const { data, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("code, name")
        .order("name");

      if (scopesError) throw scopesError;
      setPmsScopes(data || []);
    } catch (err) {
      console.error("Error loading scopes:", err);
      setError("Failed to load scopes");
    }
  };

  useEffect(() => {
    loadConnectors();
  }, []);

  const loadConnectors = async () => {
    try {
      const { data, error: queryError } = await supabase
        .from("connectors")
        .select("*")
        .order("name");

      if (queryError) throw queryError;
      setConnectors(data || []);
    } catch (err) {
      console.error("Error loading connectors:", err);
      setError("Failed to load connectors");
    } finally {
      setLoading(false);
    }
  };

  const filteredConnectors = connectors.filter((connector) => {
    // Match the code too: relation chips carry the connector code, which is not
    // always a substring of the display name.
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      connector.name.toLowerCase().includes(term) ||
      (connector.code ?? "").toLowerCase().includes(term);
    const matchesStatus =
      statusFilter === "all" || connector.status === statusFilter;
    const matchesType = typeFilter === "all" || connector.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="extranet-container">
        <div className="extranet-container-loading">
          <div className="text-gray-500">Loading connectors...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Database className="h-6 w-6 text-indigo-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Connectors</h2>
          </div>
          <button
            onClick={() =>
              setSelectedConnector({
                id: "",
                name: "",
                type: "pms",
                status: "inactive",
                hotel_base: 0,
                active_hotels: 0,
                scopes: [],
                code: "",
                created_at: "",
                updated_at: "",
              })
            }
            className="btn-primary inline-flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add New Connector
          </button>
        </div>
      </div>

      <div className="extranet-container-body">
        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-600 text-sm rounded-md">
            {error}
          </div>
        )}

        <div className="mb-6 space-y-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search connectors..."
                  className="form-input pl-10"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="form-input"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="in progress">In Progress</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="form-input"
                >
                  <option value="all">All Types</option>
                  <option value="pms">PMS</option>
                  <option value="crs">CRS</option>
                  <option value="crm">CRM</option>
                  <option value="rms">RMS</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full [&_.table-header]:px-3 [&_.table-cell]:px-3 [&_.table-cell]:whitespace-normal [&_.table-cell]:break-words">
          <table className="w-full table-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Type</th>
                <th className="table-header">Status</th>
                <th className="table-header">Hotel Base</th>
                <th className="table-header">Active Hotels</th>
                <th className="table-header">Hotels</th>
                <th className="table-header">Scopes</th>
                <th className="table-header">Trigger Objects</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredConnectors.map((connector) => (
                <tr key={connector.id} className="table-row">
                  <td className="table-cell">{connector.name}</td>
                  <td className="table-cell uppercase">{connector.type}</td>
                  <td className="table-cell">
                    <span
                      className={`status-badge ${
                        connector.status === "active"
                          ? "bg-green-100 text-green-800"
                          : connector.status === "in progress"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {connector.status}
                    </span>
                  </td>
                  <td className="table-cell">
                    {connector.hotel_base.toLocaleString()}
                  </td>
                  <td className="table-cell">
                    {connector.active_hotels.toLocaleString()}
                  </td>
                  <td className="table-cell whitespace-normal">
                    <RelationChips
                      chips={hotelChipsFor(connector.code)}
                      onSelect={
                        onOpenHotel ? (chip) => onOpenHotel(chip.label) : undefined
                      }
                      emptyLabel={connectorEmptyLabel("No hotel")}
                      emptyVariant={connectorEmptyVariant}
                    />
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-wrap gap-1">
                      {connector.scopes?.map((code) => {
                        // A scope the connector declares but pms_scopes does not
                        // define used to render as an empty chip. Show the raw code
                        // instead: the gap is a data problem worth seeing.
                        const scope = pmsScopes.find((item) => item.code === code);
                        return (
                          <span
                            key={code}
                            title={
                              scope
                                ? `${scope.name} (${code})`
                                : `Unknown scope: ${code}`
                            }
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                              scope
                                ? "bg-indigo-100 text-indigo-800"
                                : "bg-amber-100 text-amber-800 border border-amber-300"
                            }`}
                          >
                            {scope?.name || code}
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="flex flex-wrap gap-1">
                      {connector.scopes?.some(
                        (scope) => scope.toLowerCase() === "webhooks"
                      ) ? (
                        connector.trigger_objects &&
                        connector.trigger_objects.length > 0 ? (
                          connector.trigger_objects.map((object, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-800"
                            >
                              {object}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-500">
                            No trigger objects
                          </span>
                        )
                      ) : (
                        <span className="text-gray-500">--</span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <button
                      onClick={() => setSelectedConnector(connector)}
                      title="Edit"
                      aria-label="Edit"
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredConnectors.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm || statusFilter !== "all" || typeFilter !== "all"
                  ? "No connectors found matching your search criteria."
                  : "No connectors available."}
              </p>
            </div>
          )}
        </div>

        {selectedConnector && (
          <ConnectorEditForm
            connector={selectedConnector}
            onClose={() => setSelectedConnector(null)}
            onSave={loadConnectors}
          />
        )}
      </div>
    </div>
  );
}
